import { SystemRole } from "@humanex/interfaces";
import { cn } from "@humanex/ui/libs";
import { useQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { useEffect, useState } from "react";

import { accountApiClient } from "@/libs/api";
import { useAuthStore } from "@/stores/auth.store";

import { Sidebar } from "./-components/sidebar";

export const Route = createFileRoute("/_auth")({
  component: RouteComponent
});

function RouteComponent() {
  const { curUser, loggedIn, loading } = useAuthStore();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const rolesQuery = useQuery({
    queryKey: ["account", "roles"],
    queryFn: () => accountApiClient.getRoles(),
    enabled: loggedIn && !loading && !!curUser?.fbUser,
    staleTime: 5 * 60 * 1000
  });

  const rolesLoading = loggedIn && !loading && !!curUser?.fbUser && rolesQuery.isLoading;
  const isAdmin = rolesQuery.data?.includes(SystemRole.ADMIN) ?? false;

  useEffect(() => {
    if (loading || rolesLoading) return;
    else if (!loggedIn || !curUser || !curUser.fbUser) navigate({ to: "/" });
    else if (rolesQuery.isFetched && !isAdmin) navigate({ to: "/unauth" });
  }, [loggedIn, loading, curUser, rolesLoading, isAdmin, rolesQuery.isFetched]);

  if (loading || rolesLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Đang tải...</p>
        </div>
      </div>
    );
  }

  const userEmail = curUser?.fbUser?.email || "";
  const userInitial = userEmail.charAt(0).toUpperCase();

  return (
    <div
      className={cn(
        "flex h-screen w-full overflow-hidden bg-primary-50",
        "p-2 sm:p-3 md:p-4 gap-2 sm:gap-3 md:gap-4 max-w-[2200px] mx-auto"
      )}
    >
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top bar */}
        <header className="flex items-center justify-between flex-shrink-0 mt-1 mb-2">
          <button
            className="md:hidden text-gray-500 hover:text-gray-700"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>
          <span className="font-semibold text-gray-800 hidden md:block">AseanHub Admin</span>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 hidden sm:block">{userEmail}</span>
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
              {userInitial}
            </div>
          </div>
        </header>
        {/* Page content */}
        <main className="flex-1 overflow-y-auto pt-0 pb-6 scrollbar-none">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
